/**
 * Native haptic feedback for the Capacitor shell.
 *
 * Thin, intent-named wrappers around @capacitor/haptics so callers don't couple
 * to the plugin and every call is uniformly guarded. Each is a hard no-op
 * outside the Capacitor shell — importantly, that means mobile-web and MobiLoud
 * users get NO vibration (we never reach the plugin's web `navigator.vibrate`
 * fallback), so this can't introduce buzzing on the responsive site.
 *
 * Fire-and-forget and never throws: a failed/absent haptic must never interrupt
 * the action it accompanies.
 */

import { Haptics, ImpactStyle, NotificationType } from "@capacitor/haptics";
import Honeybadger from "@honeybadger-io/js";
import { isCapacitorEnvironment } from "./detection";

function reportHapticError(error) {
  try {
    Honeybadger.notify(error, {
      context: { component: "capacitor_haptics" },
    });
  } catch {
    // reporting must never break the app
  }
}

// Run a haptic only inside the shell, swallowing any error (incl. a synchronous
// throw) so it can never disrupt the user action it's reinforcing.
function runHaptic(fn) {
  if (!isCapacitorEnvironment()) {
    return;
  }
  Promise.resolve().then(fn).catch(reportHapticError);
}

/** Light tap when a set is marked complete — the core lifting loop. */
export function hapticSetComplete() {
  runHaptic(() => Haptics.impact({ style: ImpactStyle.Light }));
}

/** Light tap when a chat message is successfully sent. */
export function hapticMessageSent() {
  runHaptic(() => Haptics.impact({ style: ImpactStyle.Light }));
}

/** Success notification when a whole workout is marked complete. */
export function hapticWorkoutComplete() {
  runHaptic(() => Haptics.notification({ type: NotificationType.Success }));
}
